import { useState, useRef, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import {
  ShoppingBag,
  Database,
  LogOut,
  FileText,
  ScrollText,
  Store,
  UserRound,
  ShieldCheck,
  KeyRound,
  Download,
} from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { usePermissions } from '../../hooks/usePermissions'
import { MODULES, ACTIONS } from '../../hooks/usePermissions'
import Avatar from '../Avatar/Avatar'
import { getStorefrontOrigin, getStorefrontUrl } from '../../utils/appOrigins'
import { isHomeOrganization } from '../../utils/homeOrganization'
import { useIsMobileTablet } from '../../hooks/useIsMobileTablet'
import { usePwaInstall } from '../../utils/pwaInstall'
import './UserMenu.css'

/**
 * UserMenu — menu do usuário no ERP.
 * Conta/compras da loja ficam em www (Next); aqui só atalhos ERP + loja externa.
 */
function UserMenu() {
  const { user, logout, isAdmin, organizations, currentSchema } = useAuth()
  const { hasPermission, loading: permissionsLoading } = usePermissions()
  const navigate = useNavigate()
  const location = useLocation()
  const [isOpen, setIsOpen] = useState(false)
  const menuRef = useRef(null)
  const buttonRef = useRef(null)
  const isMobile = useIsMobileTablet()
  const { canInstall, showIosHint, promptInstall } = usePwaInstall()

  const isInsideERP = location.pathname.startsWith('/erp')
  const storefrontHome = getStorefrontOrigin()
  const storefrontOrders = getStorefrontUrl('/orders')

  const userCanAccessERP = () => {
    if (!user) return false
    if (isAdmin()) return true
    if (permissionsLoading) return false
    return hasPermission(MODULES.ERP, ACTIONS.VIEW)
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('touchstart', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
    }
  }, [isOpen])

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen])

  const toggleMenu = () => {
    setIsOpen(!isOpen)
  }

  const handleMenuItemClick = (path) => {
    setIsOpen(false)
    if (path) {
      navigate(path)
    }
  }

  const handleLogout = () => {
    setIsOpen(false)
    logout()
    navigate('/login', { replace: true })
  }

  if (!user) {
    return null
  }

  const canAccessERP = userCanAccessERP()
  const showHomeLinks = isHomeOrganization(
    organizations,
    currentSchema || user?.organization_schema
  )
  const canManageAccessGroups = hasPermission(MODULES.PEOPLE, ACTIONS.PEOPLE_ACCESS_GROUPS)
  const showInstall = isMobile && (canInstall || showIosHint)

  return (
    <div className="user-menu">
      <button
        ref={buttonRef}
        className="user-menu-button"
        onClick={toggleMenu}
        aria-label="Menu do usuário"
        aria-expanded={isOpen}
        aria-haspopup="true"
        type="button"
      >
        <Avatar
          name={user.name}
          email={user.email}
          imageUrl={user.avatar_url}
          size="md"
        />
      </button>

      {isOpen && (
        <div
          ref={menuRef}
          className="user-menu-dropdown"
          role="menu"
          aria-label="Menu de opções do usuário"
        >
          <div className="user-menu-header">
            <Avatar
              name={user.name}
              email={user.email}
              imageUrl={user.avatar_url}
              size="lg"
            />
            <div className="user-menu-info">
              <div className="user-menu-full-name">{user.name}</div>
              <div className="user-menu-email">{user.email}</div>
            </div>
          </div>

          <div className="user-menu-divider"></div>

          <nav className="user-menu-items" role="navigation">
            <Link
              to="/erp/perfil"
              className="user-menu-item"
              onClick={() => handleMenuItemClick('/erp/perfil')}
              role="menuitem"
            >
              <UserRound size={20} aria-hidden="true" />
              <span>Meu perfil</span>
            </Link>

            {(isAdmin() || hasPermission(MODULES.DIGITAL_CERTIFICATE, ACTIONS.DIGITAL_CERTIFICATE_MANAGE) || hasPermission(MODULES.SETTINGS, ACTIONS.SETTINGS_CERTIFICATES)) && (
              <Link
                to="/erp/configuracoes/certificados"
                className="user-menu-item"
                onClick={() => handleMenuItemClick('/erp/configuracoes/certificados')}
                role="menuitem"
              >
                <KeyRound size={20} aria-hidden="true" />
                <span>Certificado digital</span>
              </Link>
            )}

            {canManageAccessGroups && (
              <Link
                to="/erp/pessoas/grupos"
                className="user-menu-item"
                onClick={() => handleMenuItemClick('/erp/pessoas/grupos')}
                role="menuitem"
              >
                <ShieldCheck size={20} aria-hidden="true" />
                <span>Grupo de Acesso</span>
              </Link>
            )}

            {showHomeLinks && (
              <a
                href={storefrontHome}
                className="user-menu-item"
                onClick={() => setIsOpen(false)}
                role="menuitem"
              >
                <Store size={20} aria-hidden="true" />
                <span>Loja</span>
              </a>
            )}

            {showHomeLinks && (
              <a
                href={storefrontOrders}
                className="user-menu-item"
                onClick={() => setIsOpen(false)}
                role="menuitem"
              >
                <ShoppingBag size={20} aria-hidden="true" />
                <span>Compras</span>
              </a>
            )}

            <Link
              to="/erp/documents/my-documents"
              className="user-menu-item"
              onClick={() => handleMenuItemClick('/erp/documents/my-documents')}
              role="menuitem"
            >
              <FileText size={20} aria-hidden="true" />
              <span>Meus Documentos</span>
            </Link>

            {canAccessERP && (
              <Link
                to="/erp"
                className="user-menu-item"
                onClick={() => handleMenuItemClick('/erp')}
                role="menuitem"
              >
                <Database size={20} aria-hidden="true" />
                <span>ERP</span>
              </Link>
            )}

            {showHomeLinks && isInsideERP && canAccessERP && (
              <Link
                to="/erp/proposito-em-acao"
                className="user-menu-item"
                onClick={() => handleMenuItemClick('/erp/proposito-em-acao')}
                role="menuitem"
              >
                <ScrollText size={20} aria-hidden="true" />
                <span>Propósito em ação</span>
              </Link>
            )}

            {showInstall && <div className="user-menu-divider"></div>}

            {isMobile && canInstall && (
              <button
                className="user-menu-item"
                onClick={() => {
                  setIsOpen(false)
                  promptInstall()
                }}
                role="menuitem"
                type="button"
              >
                <Download size={20} aria-hidden="true" />
                <span>Instalar aplicativo</span>
              </button>
            )}

            {isMobile && showIosHint && !canInstall && (
              <p className="user-menu-note">
                No iPhone ou iPad, use Compartilhar e depois Adicionar à Tela de Início.
              </p>
            )}

            <div className="user-menu-divider"></div>

            <button
              className="user-menu-item user-menu-item-danger"
              onClick={handleLogout}
              role="menuitem"
              type="button"
            >
              <LogOut size={20} aria-hidden="true" />
              <span>Sair</span>
            </button>
          </nav>
        </div>
      )}
    </div>
  )
}

export default UserMenu
